import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { DeskMove } from '@/lib/deskNextMove';

interface AskCannafaxContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  toggle: () => void;
  /** A question queued by an "Ask about this" action; the panel sends it once open. */
  pendingPrompt: string | null;
  askAbout: (prompt: string) => void;
  consumePendingPrompt: () => string | null;
  /** The Your Next Move™ / Desk Setup suggestion the panel is pinned to (opened from the ribbon or Your Desk). */
  pinnedMove: DeskMove | null;
  askAboutMove: (move: DeskMove) => void;
  clearPinnedMove: () => void;
}

const AskCannafaxContext = createContext<AskCannafaxContextType>({
  isOpen: false,
  setIsOpen: () => {},
  toggle: () => {},
  pendingPrompt: null,
  askAbout: () => {},
  consumePendingPrompt: () => null,
  pinnedMove: null,
  askAboutMove: () => {},
  clearPinnedMove: () => {},
});

export function AskCannafaxProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  const [pinnedMove, setPinnedMove] = useState<DeskMove | null>(null);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const askAbout = useCallback((prompt: string) => {
    setPendingPrompt(prompt);
    setIsOpen(true);
  }, []);
  const askAboutMove = useCallback((move: DeskMove) => {
    setPinnedMove(move);
    setIsOpen(true);
  }, []);
  const clearPinnedMove = useCallback(() => setPinnedMove(null), []);
  const consumePendingPrompt = useCallback(() => {
    let taken: string | null = null;
    setPendingPrompt((prev) => {
      taken = prev;
      return null;
    });
    return taken;
  }, []);

  // ⌘K / Ctrl+K opens or closes Ask Cannafax anywhere it is mounted (Esc closes via the sheet).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // A pinned move belongs to the panel session that opened it.
  useEffect(() => {
    if (!isOpen) setPinnedMove(null);
  }, [isOpen]);

  return (
    <AskCannafaxContext.Provider value={{ isOpen, setIsOpen, toggle, pendingPrompt, askAbout, consumePendingPrompt, pinnedMove, askAboutMove, clearPinnedMove }}>
      {children}
    </AskCannafaxContext.Provider>
  );
}

export function useAskCannafax() {
  return useContext(AskCannafaxContext);
}
